import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useAccountsStore = defineStore("accounts", {
  state: () => ({
    accounts: [],
    accountTypes: [],
    selectedAccount: null,
    loading: false,
    loadingTypes: false,
    saving: false,
    error: null
  }),

  getters: {
    activeAccounts: (state) => {
      return state.accounts.filter((account) => account.is_active)
    },

    inactiveAccounts: (state) => {
      return state.accounts.filter((account) => !account.is_active)
    },

    getAccountById: (state) => {
      return (id) => state.accounts.find((account) => account.id === id)
    },

    /**
     * Get top-level accounts (those without a parent)
     */
    topLevelAccounts: (state) => {
      return state.accounts.filter((account) => !account.parent_id)
    },

    /**
     * Get accounts by type
     */
    accountsByType: (state) => {
      return (typeId) =>
        state.accounts.filter((account) => account.type_id === typeId)
    },

    /**
     * Build hierarchical tree structure for TreeTable
     * Returns array of TreeNode objects
     */
    accountsTree: (state) => {
      const buildNode = (account) => {
        const children = state.accounts.filter(
          (a) => a.parent_id === account.id
        )
        return {
          key: String(account.id),
          data: account,
          children: children.map(buildNode)
        }
      }

      // Start from top-level accounts
      return state.accounts
        .filter((account) => !account.parent_id)
        .map(buildNode)
    },

    /**
     * Flatten tree to array (for flat view with indentation info)
     */
    accountsFlat: (state) => {
      const result = []
      const addToList = (account, level = 0) => {
        result.push({ ...account, _level: level })
        const children = state.accounts.filter(
          (a) => a.parent_id === account.id
        )
        children.forEach((child) => addToList(child, level + 1))
      }

      state.accounts
        .filter((account) => !account.parent_id)
        .forEach((account) => addToList(account, 0))

      return result
    }
  },

  actions: {
    async fetchAccounts() {
      this.loading = true
      this.error = null
      try {
        // Include relations for hierarchy and type info
        const response = await apiV1.get("/accounts", {
          params: {
            with: ["accountType", "parent", "childAccounts"]
          }
        })
        this.accounts = response.data.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchAccountTypes() {
      this.loadingTypes = true
      try {
        const response = await apiV1.get("/account-types")
        this.accountTypes = response.data.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingTypes = false
      }
    },

    async fetchAccount(accountId) {
      this.error = null
      try {
        const response = await apiV1.get(`/accounts/${accountId}`)
        this.selectedAccount = response.data.data
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    async createAccount(data) {
      this.saving = true
      this.error = null
      try {
        const response = await apiV1.post("/accounts", data)
        const newAccount = response.data.data
        this.accounts.push(newAccount)
        return newAccount
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.saving = false
      }
    },

    async bulkCreateAccounts(accounts, connectorId = null, parentAccountConnectorId = null) {
      this.saving = true
      this.error = null
      try {
        const payload = { accounts }
        if (connectorId) payload.connector_id = connectorId
        if (parentAccountConnectorId) payload.parent_account_connector_id = parentAccountConnectorId
        const response = await apiV1.post("/accounts/bulk", payload)
        const created = response.data.data
        this.accounts.push(...created)
        return created
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.saving = false
      }
    },

    async updateAccount(accountId, data) {
      this.saving = true
      this.error = null
      try {
        const response = await apiV1.put(`/accounts/${accountId}`, data)
        const updatedAccount = response.data.data

        // Update the account in the list
        const index = this.accounts.findIndex((a) => a.id === accountId)
        if (index !== -1) {
          this.accounts[index] = updatedAccount
        }

        return updatedAccount
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.saving = false
      }
    },

    async deactivateAccount(accountId) {
      this.error = null
      try {
        const response = await apiV1.post(`/accounts/${accountId}/deactivate`)

        // Update the account in the list
        const index = this.accounts.findIndex((a) => a.id === accountId)
        if (index !== -1) {
          this.accounts[index] = {
            ...this.accounts[index],
            status: 0,
            is_active: false,
            status_label: "Inactive"
          }
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    async activateAccount(accountId) {
      this.error = null
      try {
        const response = await apiV1.post(`/accounts/${accountId}/activate`)
        const activatedAccount = response.data.data

        // Update the account in the list
        const index = this.accounts.findIndex((a) => a.id === accountId)
        if (index !== -1) {
          this.accounts[index] = activatedAccount
        }

        return activatedAccount
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    async deleteAccount(accountId) {
      this.error = null
      try {
        const response = await apiV1.delete(`/accounts/${accountId}`)

        // Remove the account from the list
        this.accounts = this.accounts.filter((a) => a.id !== accountId)

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    setSelectedAccount(account) {
      this.selectedAccount = account
    },

    clearSelectedAccount() {
      this.selectedAccount = null
    },

    clearError() {
      this.error = null
    }
  }
})
